import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Camera, Send, Stethoscope, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../i18n';
import { toast } from 'sonner';
import VoiceButton from '../components/VoiceButton';
import ImagePicker from '../components/ImagePicker';
import AiCaution from '../components/AiCaution';
import { fileToCompressedDataURL } from '../lib/image';

export default function Havedoktor() {
    const { t, locale } = useI18n();
    const navigate = useNavigate();
    const location = useLocation();
    const [sessionId] = useState(() => {
        const k = 'doctor_session';
        let v = localStorage.getItem(k);
        if (!v) { v = `s_${Date.now()}`; localStorage.setItem(k, v); }
        return v;
    });
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [imageDataUrl, setImageDataUrl] = useState(null);
    const [busy, setBusy] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        api.get(`/ai/history/${sessionId}`).then((r) => setMessages(r.data || [])).catch(() => setMessages([]));
    }, [sessionId]);

    useEffect(() => {
        const plantName = location.state?.plantName;
        if (!plantName) return;
        setInput(locale === 'da'
            ? `Hjælp mig med at vurdere og behandle ${plantName}. Jeg kan tilføje et nyt billede her.`
            : `Help me assess and treat ${plantName}. I can add a new photo here.`);
    }, [location.state, locale]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, busy]);

    const pickImage = async (e) => {
        const f = e?.target ? e.target.files?.[0] : e;
        if (!f) return;
        try {
            const compressed = await fileToCompressedDataURL(f);
            setImageDataUrl(compressed);
        } catch {
            const r = new FileReader();
            r.onload = () => setImageDataUrl(r.result);
            r.readAsDataURL(f);
        }
    };

    const sendMessage = async (text) => {
        if (!text?.trim() && !imageDataUrl) return;
        const userMsg = { id: 'u_' + Date.now(), role: 'user', content: text || '(image)', image_data: imageDataUrl };
        setMessages((m) => [...m, userMsg]);
        const payload = { session_id: sessionId, message: text || 'Identify or diagnose the plant in the image.', image_base64: imageDataUrl, locale };
        setInput(''); setImageDataUrl(null);
        setBusy(true);
        try {
            const r = await api.post('/ai/chat', payload);
            setMessages((m) => [...m, { id: 'a_' + Date.now(), role: 'assistant', content: r.data.reply }]);
        } catch (err) {
            const st = err?.response?.status;
            if (st !== 401 && st !== 403) toast.error(t('doctor.unavailable'));
        } finally { setBusy(false); }
    };

    const send = async (e) => {
        e?.preventDefault?.();
        return sendMessage(input);
    };

    const suggestions = ['doctor.s1', 'doctor.s2', 'doctor.s3', 'doctor.s4'];

    const diagnoseImage = async () => {
        if (!imageDataUrl) return;
        setBusy(true);
        const userMsg = { id: 'u_' + Date.now(), role: 'user', content: t('doctor.diagnose_label'), image_data: imageDataUrl };
        setMessages((m) => [...m, userMsg]);
        try {
            const r = await api.post('/ai/identify', { image_base64: imageDataUrl, mode: 'diagnose', locale });
            const d = r.data;
            const reply = d.diagnosis
                ? `**${d.diagnosis}** (${d.severity || 'medium'} · ${d.confidence || '–'}%)\n\n` +
                  (d.causes?.length ? `**${t('doctor.causes')}**\n${d.causes.map((c) => `• ${c}`).join('\n')}\n\n` : '') +
                  (d.actions?.length ? `**${t('doctor.actions')}**\n${d.actions.map((a, i) => `${i + 1}. ${a}`).join('\n')}\n\n` : '') +
                  (d.prevention ? `**${t('doctor.prevention')}** ${d.prevention}` : '')
                : (d.raw || t('doctor.no_diagnosis'));
            setMessages((m) => [...m, { id: 'a_' + Date.now(), role: 'assistant', content: reply }]);
            setImageDataUrl(null);
        } catch (err) {
            const dt = err?.response?.data?.detail;
            if (err?.response?.status === 402) {
                toast.error(typeof dt === 'string' ? dt : t('doctor.premium_required'));
                navigate('/premium');
            } else if (err?.response?.status !== 401 && err?.response?.status !== 403) {
                toast.error(typeof dt === 'string' ? dt : t('doctor.diag_failed'));
            }
        } finally { setBusy(false); }
    };

    return (
        <div className="px-5 pt-10 pb-40 max-w-2xl mx-auto min-h-screen flex flex-col">
            <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-2xl bg-sky-400 text-white flex items-center justify-center">
                    <Stethoscope size={18} strokeWidth={1.8} />
                </div>
                <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">AI · Claude Sonnet</p>
                    <h1 className="font-serif text-2xl text-brand-ink">{t('doctor.title')}</h1>
                </div>
            </div>
            <p className="text-sm text-brand-ink2 mb-3">{t('doctor.subtitle')}</p>
            <AiCaution className="mb-6" />

            <div className="flex-1 space-y-3 mb-4">
                {messages.length === 0 && !busy && (
                    <div className="bg-brand-card border border-brand-border rounded-3xl p-6">
                        <Sparkles size={18} className="text-brand-green mb-2" />
                        <p className="text-sm text-brand-ink2 mb-4">{t('doctor.try_q')}</p>
                        <div className="space-y-2">
                            {suggestions.map((key) => (
                                <button data-testid={`suggestion-${key}`} key={key} onClick={() => setInput(t(key))} className="block w-full text-left text-sm bg-brand-bg2 hover:bg-brand-border px-4 py-2.5 rounded-2xl transition-colors text-brand-ink">
                                    {t(key)}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {messages.map((m) => (
                    <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                            data-testid={`chat-msg-${m.role}`}
                            className={`max-w-[85%] px-4 py-3 rounded-2xl ${
                                m.role === 'user'
                                    ? 'bg-brand-bg2 text-brand-ink rounded-br-md'
                                    : 'bg-brand-card border border-brand-border text-brand-ink rounded-bl-md'
                            }`}
                        >
                            {m.image_data && <img src={m.image_data} alt="" className="rounded-xl mb-2 max-w-[240px]" />}
                            <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
                        </div>
                    </div>
                ))}

                {busy && (
                    <div className="flex justify-start">
                        <div className="bg-brand-card border border-brand-border rounded-2xl px-4 py-3 text-sm text-brand-ink2">
                            <span className="inline-flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse" />
                                <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse [animation-delay:120ms]" />
                                <span className="w-1.5 h-1.5 bg-brand-green rounded-full animate-pulse [animation-delay:240ms]" />
                                <span className="ml-2">{t('doctor.thinking')}</span>
                            </span>
                        </div>
                    </div>
                )}

                <div ref={endRef} />
            </div>

            {/* Sticky input */}
            <form onSubmit={send} className="fixed bottom-[calc(88px_+_var(--safe-bottom))] left-0 right-0 px-5 z-40">
                <div className="max-w-2xl mx-auto">
                    {imageDataUrl && (
                        <div className="mb-2 inline-flex items-center gap-2 bg-brand-card border border-brand-border rounded-2xl p-2 pr-3">
                            <img src={imageDataUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                            <button data-testid="btn-diagnose-image" type="button" onClick={diagnoseImage} disabled={busy} className="text-xs bg-brand-terracotta text-white px-3 py-1.5 rounded-full hover:opacity-90 disabled:opacity-50">
                                {t('doctor.diagnose')}
                            </button>
                            <button type="button" onClick={() => setImageDataUrl(null)} className="text-xs text-brand-ink3 hover:text-brand-terracotta">{t('doctor.remove')}</button>
                        </div>
                    )}
                    <div className="bg-brand-card border border-brand-border rounded-full px-2 py-2 flex items-center gap-1 shadow-sm">
                        <ImagePicker testId="doctor-pick" onSelect={pickImage}>
                            {(openPicker) => (
                                <button type="button" data-testid="btn-doctor-pick" onClick={openPicker} className="p-2 rounded-full hover:bg-brand-bg2" title={t('doctor.upload_photo')}>
                                    <Camera size={18} strokeWidth={1.6} className="text-brand-ink2" />
                                </button>
                            )}
                        </ImagePicker>
                        <VoiceButton
                            locale={locale}
                            onTranscript={(text) => setInput(text)}
                            onAutoSend={async (text) => {
                                if (!text.trim()) return;
                                setInput(text);
                                // Trigger send programmatically
                                await sendMessage(text);
                            }}
                        />
                        <input
                            data-testid="input-doctor-message"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={t('doctor.placeholder')}
                            className="flex-1 bg-transparent px-2 py-2 text-sm focus:outline-none"
                        />
                        <button data-testid="btn-doctor-send" type="submit" disabled={busy} className="bg-brand-green disabled:opacity-50 text-white p-2.5 rounded-full hover:bg-brand-greenDark">
                            <Send size={15} strokeWidth={2} />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}
